import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    ArrowRight, MessageSquare, Building2, GitBranch,
    Bell, ShieldCheck, BarChart3, Layers, Workflow, BookOpenText,
    CheckCircle2, Sparkles, Tag, UserCog, Clock, ArrowUpRight, FileText,
    ArrowUp
} from 'lucide-react';
import { LandingNav } from '../components/LandingNav';
import { LandingFooter } from '../components/LandingFooter';
import './landingPage.css';

const Section: React.FC<{ id?: string; className?: string; children: React.ReactNode }> = ({ id, className = '', children }) => (
    <section id={id} className={`raabta-section ${className}`}>
        <div className="raabta-container">{children}</div>
    </section>
);

export const LandingPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showScrollTop, setShowScrollTop] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 300) {
                setShowScrollTop(true);
            } else {
                setShowScrollTop(false);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    useEffect(() => {
        if (location.hash) {
            const id = location.hash.replace('#', '');
            const element = document.getElementById(id);
            if (element) {
                const timer = setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });
                }, 150);
                return () => clearTimeout(timer);
            }
        }
    }, [location.hash]);

    useEffect(() => {
        // Reveal-on-scroll
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                    }
                });
            },
            { threshold: 0.12 }
        );
        document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <div className="raabta-landing">
            {/* ── Navigation ── */}
            <LandingNav showMenu />

            {/* ── Hero ── */}
            <Section className="raabta-hero">
                <div className="raabta-hero__grid">
                    <div className="raabta-hero__content reveal">
                        <span className="raabta-eyebrow">
                            <Sparkles size={13} /> Built for modern schools
                        </span>
                        <h1 className="raabta-hero__title">
                            Connect parents, staff, and admins. <span className="raabta-gradient-text">Without the chaos.</span>
                        </h1>
                        <p className="raabta-hero__subtitle">
                            Raabta is a complete ticketing and communication platform for schools.
                            Route parent queries to the right people, track every conversation,
                            and resolve issues with workflows that actually scale.
                        </p>
                        {/* <div className="raabta-hero__cta">
                            <button className="raabta-btn raabta-btn--primary raabta-btn--lg" onClick={() => navigate('/login')}>
                                Start your school <ArrowRight size={16} />
                            </button>
                            <button className="raabta-btn raabta-btn--ghost raabta-btn--lg" onClick={() => navigate('/login')}>
                                Sign in
                            </button>
                        </div> */}
                        <div className="raabta-hero__trust">
                            <div className="raabta-trust-item">
                                <CheckCircle2 size={14} color="#10b981" /> Multi-tenant ready
                            </div>
                            <div className="raabta-trust-item">
                                <CheckCircle2 size={14} color="#10b981" /> Custom workflows
                            </div>
                            <div className="raabta-trust-item">
                                <CheckCircle2 size={14} color="#10b981" /> Knowledge hub built-in
                            </div>
                        </div>
                    </div>
                    <div className="raabta-hero__visual reveal">
                        <HeroPreview />
                    </div>
                </div>
            </Section>

            {/* ── Stats ── */}
            <Section className="raabta-stats">
                <div className="raabta-stats__grid">
                    {[
                        { value: '90%', label: 'Faster query resolution' },
                        { value: '40+', label: 'Partner schools' },
                        { value: '95%', label: 'Parent satisfaction' },
                        { value: '< 2 hrs', label: 'Average response time' },
                    ].map((s) => (
                        <div key={s.label} className="raabta-stat reveal">
                            <div className="raabta-stat__value">{s.value}</div>
                            <div className="raabta-stat__label">{s.label}</div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* ── Features Grid ── */}
            <Section id="features" className="raabta-features">
                <div className="raabta-section-header reveal">
                    <span className="raabta-eyebrow"><Layers size={13} /> Features</span>
                    <h2 className="raabta-section-title">Everything your school needs to communicate</h2>
                    <p className="raabta-section-subtitle">
                        From the first parent ticket to the last resolved query, Raabta
                        handles the full lifecycle of school operations.
                    </p>
                </div>
                <div className="raabta-features__grid">
                    {[
                        { icon: <MessageSquare size={20} />, title: 'Smart Ticketing', desc: 'Parents raise queries, staff resolves them. Every conversation is tracked, searchable, and SLA-bound.', color: '#6366f1' },
                        { icon: <Workflow size={20} />, title: 'Custom Workflows', desc: 'Design multi-level approval flows per department. Tickets escalate automatically when SLAs slip.', color: '#0ea5e9' },
                        { icon: <Building2 size={20} />, title: 'Department Routing', desc: 'Tickets land in the right inbox. Reassign to the correct department in two clicks if needed.', color: '#10b981' },
                        { icon: <UserCog size={20} />, title: 'Role-Based Access', desc: 'Super admins, school admins, and employees each see what they need. Permissions, baked in.', color: '#f59e0b' },
                        { icon: <Bell size={20} />, title: 'Real-time Notifications', desc: 'Stay on top of assignments, escalations, mentions, and SLA warnings as they happen.', color: '#ec4899' },
                        { icon: <BookOpenText size={20} />, title: 'AI Knowledge Hub', desc: 'Upload handbooks and policies. Power an AI assistant that answers parents instantly.', color: '#8b5cf6' },
                        { icon: <BarChart3 size={20} />, title: 'Live Dashboards', desc: 'Drill into ticket counts by status, department, and team. Spot bottlenecks before they grow.', color: '#06b6d4' },
                        { icon: <Tag size={20} />, title: 'Tag & Categorize', desc: 'Group tickets by class, topic, or urgency. Build queryable history for every situation.', color: '#dc2626' },
                        { icon: <ShieldCheck size={20} />, title: 'Audit Trails', desc: 'Every reply, escalation, and reassignment is logged. Full transparency, no exceptions.', color: '#0891b2' },
                    ].map((f) => (
                        <div key={f.title} className="raabta-feature reveal">
                            <div className="raabta-feature__icon" style={{ background: `${f.color}15`, color: f.color }}>{f.icon}</div>
                            <h3 className="raabta-feature__title">{f.title}</h3>
                            <p className="raabta-feature__desc">{f.desc}</p>
                        </div>
                    ))}
                </div>
            </Section>

            {/* ── Workflow Steps (GIF-like animated) ── */}
            <Section id="workflow" className="raabta-workflow">
                <div className="raabta-section-header reveal">
                    <span className="raabta-eyebrow"><GitBranch size={13} /> How it works</span>
                    <h2 className="raabta-section-title">From parent question to resolved ticket</h2>
                    {/* <p className="raabta-section-subtitle">A ticket flows through the right hands automatically.</p> */}
                </div>
                <HowItWorksFlow />
            </Section>

            {/* ── Roles Section ── */}
            {/* <Section id="roles" className="raabta-roles">
                <div className="raabta-section-header reveal">
                    <span className="raabta-eyebrow"><Users size={13} /> Built for every role</span>
                    <h2 className="raabta-section-title">One platform, four perspectives</h2>
                </div>
                <div className="raabta-roles__grid">
                    {[
                        { icon: <ShieldCheck size={22} />, role: 'Super Admin', desc: 'Manage every school on the platform, monitor health, and onboard new tenants.', color: '#6366f1', items: ['Multi-school dashboards', 'Tenant management', 'Platform analytics'] },
                        { icon: <UserCog size={22} />, role: 'School Admin', desc: 'Configure departments, employees, parents, workflows, and the AI knowledge base.', color: '#0ea5e9', items: ['Workflow designer', 'Team management', 'School branding'] },
                        { icon: <GraduationCap size={22} />, role: 'Employee', desc: 'See assigned tickets, reply to parents, escalate when needed, and resolve fast.', color: '#10b981', items: ['Personal queue', 'SLA reminders', 'Quick resolution'] },
                        { icon: <Heart size={22} />, role: 'Parent', desc: 'Ask questions, track ticket status, and chat with the AI assistant for instant answers.', color: '#f59e0b', items: ['Self-service portal', 'AI-powered help', 'Real-time updates'] },
                    ].map((r) => (
                        <div key={r.role} className="raabta-role reveal" style={{ '--role-color': r.color } as React.CSSProperties}>
                            <div className="raabta-role__icon" style={{ background: `${r.color}15`, color: r.color }}>{r.icon}</div>
                            <div className="raabta-role__name">{r.role}</div>
                            <p className="raabta-role__desc">{r.desc}</p>
                            <ul className="raabta-role__list">
                                {r.items.map((item) => (
                                    <li key={item}><CheckCircle2 size={13} color={r.color} /> {item}</li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </Section> */}

            {/* ── Pricing ── */}
            {/* <Section id="pricing" className="raabta-pricing">
                <div className="raabta-section-header reveal">
                    <span className="raabta-eyebrow"><Tag size={13} /> Pricing</span>
                    <h2 className="raabta-section-title">Simple plans, no hidden fees</h2>
                    <p className="raabta-section-subtitle">Start free. Scale when your school grows.</p>
                </div>
                <div className="raabta-pricing__grid">
                    {[
                        { name: 'Starter', price: 'Free', period: 'forever', desc: 'For small schools getting started', features: ['Up to 50 parents', '1 department', 'Basic workflows', 'Email support'], cta: 'Start free', featured: false },
                        { name: 'Pro', price: '$49', period: 'per month', desc: 'For growing schools that need more', features: ['Unlimited parents & students', 'Unlimited departments', 'Advanced workflows', 'AI knowledge hub', 'Priority support'], cta: 'Start trial', featured: true },
                        { name: 'Enterprise', price: 'Custom', period: 'tailored', desc: 'For large institutions', features: ['Custom integrations', 'Dedicated SLA', 'White-label branding', 'On-premise option', '24/7 phone support'], cta: 'Contact sales', featured: false },
                    ].map((p) => (
                        <div key={p.name} className={`raabta-plan reveal ${p.featured ? 'raabta-plan--featured' : ''}`}>
                            {p.featured && <span className="raabta-plan__badge">Most Popular</span>}
                            <div className="raabta-plan__name">{p.name}</div>
                            <div className="raabta-plan__price">
                                <span className="raabta-plan__price-value">{p.price}</span>
                                <span className="raabta-plan__price-period">/{p.period}</span>
                            </div>
                            <p className="raabta-plan__desc">{p.desc}</p>
                            <ul className="raabta-plan__features">
                                {p.features.map((f) => (
                                    <li key={f}><CheckCircle2 size={14} /> {f}</li>
                                ))}
                            </ul>
                            <button className={`raabta-btn ${p.featured ? 'raabta-btn--primary' : 'raabta-btn--outline'} raabta-btn--full`} onClick={() => navigate('/login')}>
                                {p.cta}
                            </button>
                        </div>
                    ))}
                </div>
            </Section> */}

            {/* ── CTA ── */}
            <Section className="raabta-cta">
                <div className="raabta-cta__card reveal">
                    <h2 className="raabta-cta__title">Ready to streamline your school's communication?</h2>
                    <p className="raabta-cta__subtitle">Set up your school in under 10 minutes. No credit card required.</p>
                    <div className="raabta-cta__buttons">
                        <button className="raabta-btn raabta-btn--primary raabta-btn--lg" onClick={() => navigate('/contact')}>
                            Get started free <ArrowRight size={16} />
                        </button>
                        {/* <button className="raabta-btn raabta-btn--ghost raabta-btn--lg" onClick={() => navigate('/login')}>
                            <Mail size={14} /> Book a demo
                        </button> */}
                    </div>
                </div>
            </Section>

            {/* ── Footer ── */}
            <LandingFooter />

            {/* ── Scroll To Top ── */}
            <button
                className={`raabta-scroll-top ${showScrollTop ? 'raabta-scroll-top--visible' : ''}`}
                onClick={scrollToTop}
                aria-label="Scroll to top"
            >
                <ArrowUp size={20} />
            </button>
        </div>
    );
};

/* ───────────────────────────── Hero Visual Preview ───────────────────────────── */
const HeroPreview: React.FC = () => {
    return (
        <div className="raabta-preview">
            {/* Background glows */}
            <div className="raabta-preview__glow raabta-preview__glow--1" />
            <div className="raabta-preview__glow raabta-preview__glow--2" />

            {/* Main mock dashboard */}
            <div className="raabta-preview__card">
                <div className="raabta-preview__header">
                    <div className="raabta-preview__dot" style={{ background: '#ef4444' }} />
                    <div className="raabta-preview__dot" style={{ background: '#f59e0b' }} />
                    <div className="raabta-preview__dot" style={{ background: '#10b981' }} />
                    <span className="raabta-preview__url">raabta.app/dashboard</span>
                </div>
                <div className="raabta-preview__body">
                    <div className="raabta-preview__title">School Dashboard</div>
                    <div className="raabta-preview__stats">
                        {[
                            { label: 'Total Tickets', value: '247', icon: <FileText size={14} />, color: '#6366f1' },
                            { label: 'Resolved', value: '218', icon: <CheckCircle2 size={14} />, color: '#10b981' },
                            { label: 'Open', value: '23', icon: <Clock size={14} />, color: '#f59e0b' },
                            { label: 'Escalated', value: '6', icon: <ArrowUpRight size={14} />, color: '#ef4444' },
                        ].map((s) => (
                            <div key={s.label} className="raabta-preview__stat">
                                <div style={{ color: s.color }}>{s.icon}</div>
                                <div className="raabta-preview__stat-value">{s.value}</div>
                                <div className="raabta-preview__stat-label">{s.label}</div>
                            </div>
                        ))}
                    </div>
                    <div className="raabta-preview__list">
                        {[
                            { name: 'Bus delay complaint', dept: 'Transport', status: 'open', color: '#16a34a' },
                            { name: 'Math homework query', dept: 'Academics', status: 'in progress', color: '#3b82f6' },
                            { name: 'Sports kit request', dept: 'Sports', status: 'resolved', color: '#0891b2' },
                        ].map((t) => (
                            <div key={t.name} className="raabta-preview__row">
                                <div>
                                    <div className="raabta-preview__row-name">{t.name}</div>
                                    <div className="raabta-preview__row-dept">{t.dept}</div>
                                </div>
                                <span className="raabta-preview__pill" style={{ background: `${t.color}1f`, color: t.color }}>
                                    <span className="raabta-preview__pill-dot" style={{ background: t.color }} />
                                    {t.status}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Floating notification card */}
            <div className="raabta-preview__notif">
                <div className="raabta-preview__notif-icon"><Bell size={14} /></div>
                <div>
                    <div className="raabta-preview__notif-title">New ticket assigned</div>
                    <div className="raabta-preview__notif-msg">"Computer exam discrepancy"</div>
                </div>
            </div>

            {/* Floating workflow chip */}
            {/* <div className="raabta-preview__chip">
                <Workflow size={12} />
                <span>Workflow: General Support</span>
                <CheckCircle2 size={12} color="#10b981" />
            </div> */}
        </div>
    );
};

/* ───────────────────────────── How It Works — Modern Flow ───────────────────────────── */
const HIW_STEPS = [
    {
        n: 1,
        icon: <MessageSquare size={22} />,
        title: 'Submit Query',
        desc: 'Parent raises a question via app, chat, or AI assistant',
        color: '#818cf8',
        gradient: 'linear-gradient(135deg, #6366f1, #818cf8)',
    },
    {
        n: 2,
        icon: <Building2 size={22} />,
        title: 'Smart Routing',
        desc: 'Auto-routed to the right department instantly',
        color: '#38bdf8',
        gradient: 'linear-gradient(135deg, #0ea5e9, #38bdf8)',
    },
    {
        n: 3,
        icon: <Workflow size={22} />,
        title: 'Workflow Assignment',
        desc: 'Multi-level approval with SLA tracking',
        color: '#fbbf24',
        gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
    },
    {
        n: 4,
        icon: <CheckCircle2 size={22} />,
        title: 'Resolved & Logged',
        desc: 'Ticket closed, parent notified, audit trail sealed',
        color: '#34d399',
        gradient: 'linear-gradient(135deg, #10b981, #34d399)',
    },
];

const HowItWorksFlow: React.FC = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [prevStep, setPrevStep] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    // Intersection observer for entrance animation
    useEffect(() => {
        const el = rootRef.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); obs.disconnect(); } },
            { threshold: 0.15 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    // Auto-cycle with direction tracking
    useEffect(() => {
        const interval = setInterval(() => {
            setActiveStep((prev) => {
                setPrevStep(prev);
                return (prev + 1) % HIW_STEPS.length;
            });
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    const handleStepClick = (idx: number) => {
        setPrevStep(activeStep);
        setActiveStep(idx);
    };

    const slideDir = activeStep > prevStep ? 'fwd' : 'bwd';

    return (
        <div ref={rootRef} className={`hiw-flow ${isVisible ? 'hiw-flow--visible' : ''}`}>
            {/* Background effects */}
            <div className="hiw-flow__bg">
                <div className="hiw-flow__orb hiw-flow__orb--1" />
                <div className="hiw-flow__orb hiw-flow__orb--2" />
                <div className="hiw-flow__orb hiw-flow__orb--3" />
            </div>

            {/* Timeline */}
            <div className="hiw-flow__timeline">
                {/* Nodes + Connectors */}
                {HIW_STEPS.map((step, idx) => (
                    <React.Fragment key={step.n}>
                        <div
                            className={`hiw-flow__node hiw-flow__node--enter-${idx} ${idx === activeStep ? 'hiw-flow__node--active' : ''} ${idx < activeStep ? 'hiw-flow__node--done' : ''}`}
                            onClick={() => handleStepClick(idx)}
                        >
                            {/* Glow effect */}
                            {idx === activeStep && (
                                <div className="hiw-flow__glow" style={{ background: `radial-gradient(circle, ${step.color}40 0%, transparent 70%)` }} />
                            )}

                            {/* Circle */}
                            <div className="hiw-flow__circle" style={idx <= activeStep ? { background: step.gradient } : {}}>
                                {idx < activeStep ? (
                                    <CheckCircle2 size={18} color="white" strokeWidth={2.5} />
                                ) : (
                                    <>
                                        <div className="hiw-flow__icon">{step.icon}</div>
                                        {idx === activeStep && <div className="hiw-flow__pulse" />}
                                    </>
                                )}
                            </div>

                            {/* Label */}
                            <div className="hiw-flow__label">
                                <span className="hiw-flow__num">{String(step.n).padStart(2, '0')}</span>
                                <span className="hiw-flow__title">{step.title}</span>
                            </div>
                        </div>

                        {/* Arrow connector between steps */}
                        {idx < HIW_STEPS.length - 1 && (
                            <div className={`hiw-flow__connector hiw-flow__connector--enter-${idx} ${idx < activeStep ? 'hiw-flow__connector--done' : ''} ${idx === activeStep ? 'hiw-flow__connector--active' : ''}`}>
                                <div className="hiw-flow__connector-line" style={idx < activeStep ? { background: HIW_STEPS[idx].gradient } : idx === activeStep ? { background: `linear-gradient(90deg, ${HIW_STEPS[idx].color}, ${HIW_STEPS[idx + 1].color}50)` } : {}} />
                                <div className="hiw-flow__connector-arrow" style={idx < activeStep ? { color: HIW_STEPS[idx].color } : idx === activeStep ? { color: HIW_STEPS[idx].color } : {}}>
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                        <path d="M3 1L9 6L3 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>

                                {/* Flying ticket card */}
                                {idx === activeStep && (
                                    <div
                                        className="hiw-flow__ticket"
                                        style={{
                                            '--ticket-color': HIW_STEPS[idx].color,
                                            '--ticket-gradient': HIW_STEPS[idx].gradient,
                                            '--ticket-next-color': HIW_STEPS[idx + 1].color,
                                        } as React.CSSProperties}
                                    >
                                        {/* Motion trail */}
                                        <div className="hiw-flow__ticket-trail" />

                                        {/* Float wrapper for bob + rotation */}
                                        <div className="hiw-flow__ticket-float">
                                            <div className="hiw-flow__ticket-body">
                                                <div className="hiw-flow__ticket-icon">
                                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <rect x="2" y="5" width="20" height="14" rx="2" />
                                                        <path d="M2 10h20" />
                                                    </svg>
                                                </div>
                                                <div className="hiw-flow__ticket-lines">
                                                    <span className="hiw-flow__ticket-line" />
                                                    <span className="hiw-flow__ticket-line hiw-flow__ticket-line--short" />
                                                </div>
                                            </div>
                                            {/* Perforated edge */}
                                            <div className="hiw-flow__ticket-perf">
                                                <span /><span /><span />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </div>

            {/* Active step card */}
            <div className={`hiw-flow__card hiw-flow__card--${slideDir}`} key={activeStep}>
                {/* Colored accent sidebar */}
                <div className="hiw-flow__card-accent" style={{ background: HIW_STEPS[activeStep].gradient }} />

                {/* Subtle color wash */}
                <div className="hiw-flow__card-wash" style={{ background: `radial-gradient(ellipse at 0% 50%, ${HIW_STEPS[activeStep].color}10 0%, transparent 65%)` }} />

                <div className="hiw-flow__card-body">
                    {/* Icon + number column */}
                    <div className="hiw-flow__card-visual">
                        <div className="hiw-flow__card-icon" style={{ background: HIW_STEPS[activeStep].gradient }}>
                            {HIW_STEPS[activeStep].icon}
                        </div>
                        <span className="hiw-flow__card-num" style={{ color: HIW_STEPS[activeStep].color }}>
                            {String(HIW_STEPS[activeStep].n).padStart(2, '0')}
                        </span>
                    </div>

                    {/* Text content */}
                    <div className="hiw-flow__card-text">
                        <div className="hiw-flow__card-step">Step {HIW_STEPS[activeStep].n} of 4</div>
                        <h3 className="hiw-flow__card-title">{HIW_STEPS[activeStep].title}</h3>
                        <p className="hiw-flow__card-desc">{HIW_STEPS[activeStep].desc}</p>
                    </div>

                    {/* Status badge */}
                    <div className="hiw-flow__card-badge" style={{ background: `${HIW_STEPS[activeStep].color}12`, color: HIW_STEPS[activeStep].color, borderColor: `${HIW_STEPS[activeStep].color}30` }}>
                        <span className="hiw-flow__badge-dot" style={{ background: HIW_STEPS[activeStep].color }} />
                        Active
                    </div>
                </div>

                {/* Progress bar */}
                <div className="hiw-flow__progress">
                    {HIW_STEPS.map((step, idx) => (
                        <div
                            key={step.n}
                            className={`hiw-flow__progress-seg ${idx === activeStep ? 'hiw-flow__progress-seg--active' : ''} ${idx < activeStep ? 'hiw-flow__progress-seg--done' : ''}`}
                            onClick={() => handleStepClick(idx)}
                        >
                            <div
                                className="hiw-flow__progress-fill"
                                style={idx <= activeStep ? { background: step.gradient, width: '100%' } : { width: '100%' }}
                            />
                            {idx === activeStep && <div className="hiw-flow__progress-shimmer" style={{ background: `linear-gradient(90deg, transparent, ${step.color}40, transparent)` }} />}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
